import { Box, Chip, CircularProgress, Stack, Typography } from "@mui/material"

import { RELATIONSHIP_LABELS, type RelationshipFacet } from "./facets"

export interface SourceChipDescriptor {
  key: string
  label: string
  onRemove: () => void
}

/** Builds one removable chip per selected relationship, one per selected
 * contact group (by name), and one per selected industry label — each
 * chip's remover deselects just that one source. */
export function buildSourceChips(
  relationships: RelationshipFacet[],
  onRemoveRelationship: (facet: RelationshipFacet) => void,
  groupIds: number[],
  groupNameById: Map<number, string>,
  onRemoveGroup: (groupId: number) => void,
  industries: string[],
  onRemoveIndustry: (industry: string) => void,
): SourceChipDescriptor[] {
  const chips: SourceChipDescriptor[] = []

  for (const facet of relationships) {
    chips.push({
      key: `relationship:${facet}`,
      label: RELATIONSHIP_LABELS[facet],
      onRemove: () => onRemoveRelationship(facet),
    })
  }
  for (const groupId of groupIds) {
    chips.push({
      key: `group:${groupId}`,
      label: groupNameById.get(groupId) ?? `Group #${groupId}`,
      onRemove: () => onRemoveGroup(groupId),
    })
  }
  for (const industry of industries) {
    chips.push({
      key: `industry:${industry}`,
      label: industry,
      onRemove: () => onRemoveIndustry(industry),
    })
  }

  return chips
}

export interface SelectionSummaryProps {
  sourceChips: SourceChipDescriptor[]
  /** Count of hand-added individuals (search / manual email). */
  individualCount: number
  /** Count of source-included people the operator unchecked. */
  excludedCount: number
  preview: { total: number; deduped: number; duplicates: number } | null
  previewLoading: boolean
  disabled?: boolean
}

/**
 * The "To" summary of the Audience Builder: a prominent live "N people will
 * receive this campaign" total (the server-resolved, deduped
 * `fetchAudiencePreview` count), chips for every selected source
 * (relationship / group / industry — each removable), and an
 * "N individually added / M unchecked" note. Numbers are rounded.
 */
export function SelectionSummary({
  sourceChips,
  individualCount,
  excludedCount,
  preview,
  previewLoading,
  disabled = false,
}: SelectionSummaryProps) {
  const hasAnySelection = sourceChips.length > 0 || individualCount > 0

  const noteParts: string[] = []
  if (individualCount > 0) {
    noteParts.push(`${individualCount.toLocaleString()} individually added`)
  }
  if (excludedCount > 0) {
    noteParts.push(`${excludedCount.toLocaleString()} unchecked`)
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {previewLoading ? (
          <CircularProgress size={14} />
        ) : preview ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            To: {Math.round(preview.deduped).toLocaleString()} people will receive this campaign
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            No audience selected yet.
          </Typography>
        )}
      </Box>

      {hasAnySelection && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {sourceChips.map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              color="primary"
              variant="outlined"
              {...(disabled ? {} : { onDelete: chip.onRemove })}
            />
          ))}
          {noteParts.length > 0 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {noteParts.join(" · ")}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  )
}

export default SelectionSummary
